import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Select,
} from "antd";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import validateMessages from "../../utils/validate";
import { Currency, PaymentMethod } from "../../shared/constant";
import globalUtils from "../../utils/globalUtils";
import { RootState } from "../../redux/rootReducer";
import { createPayment } from "../../redux/actions/paymentActions";
import retrieveCurrencies from "../../redux/actions/currencyActions";
import ROUTES from "../../routes";
import { resetClient } from "../../redux/actions/accountActions";
import dateUtils from "../../utils/dateUtils";

const { Option } = Select;

interface ModalFormProps {
  visible: boolean;
  onCancel: () => void;
}

function CreatePaymentModal({ visible, onCancel }: ModalFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { invoiceId } = useParams();
  const { invoiceDetails } = useSelector((state: RootState) => state.invoice);
  const { pending, createPaymentSuccess } = useSelector(
    (state: RootState) => state.payment,
  );
  const { currencies } = useSelector((state: RootState) => state.currency);

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(retrieveCurrencies());

    form.setFieldsValue({
      client: invoiceDetails.client_details.name,
      currency: invoiceDetails.display_currency,
      exchange_rate:
        invoiceDetails?.display_currency === Currency.BDT ? 1 : undefined,
      amount: invoiceDetails.invoice.invoice_amount,

      account_name: invoiceDetails.client_bank_details.account_name,
      account_no: invoiceDetails.client_bank_details.account_no,
      bank_name: invoiceDetails.client_bank_details.bank,
      branch: invoiceDetails.client_bank_details.branch,
      routing_no: invoiceDetails.client_bank_details.routing_no,
      phone: invoiceDetails.client_bank_details.phone,
    });
  }, []);

  const displayPaymentSuccess = () => {
    onCancel();
    navigate(ROUTES.PATHS.ADMIN.PAYMENTS);
    message.success("Payment created successfully");
    /* reseting paymentWithAdjustmentSuccess to false */
    dispatch(resetClient());
  };

  useEffect(() => {
    if (createPaymentSuccess) displayPaymentSuccess();
  }, [pending]);

  const getPaymentTitle = () => {
    return `${invoiceDetails.client_details.name} ${
      invoiceDetails.from_date
        ? `(${dateUtils.defaultFormat(
            invoiceDetails.from_date,
          )} - ${dateUtils.defaultFormat(invoiceDetails.to_date)})`
        : ""
    } - ${invoiceDetails.display_currency}`;
  };

  const onCreatePayment = async () => {
    try {
      await form.validateFields();
    } catch (errorInfo) {
      return;
    }

    const formFields = form.getFieldsValue();

    const payload = {
      invoice: invoiceId,
      ...(formFields.exchange_rate && {
        exchange_rate: formFields.exchange_rate,
      }),
      title: getPaymentTitle(),
      payment_method: formFields.payment_method,
      payment_date: dateUtils.defaultFormat(formFields.payment_date),
    };

    dispatch(createPayment(payload));
  };

  return (
    <Modal
      visible={visible}
      title="Create Payment"
      centered
      width={740}
      onOk={onCreatePayment}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={pending as boolean}
          onClick={onCreatePayment}
        >
          Create
        </Button>,
      ]}
    >
      <div className="mx-6 mt-6 mb-[14px]">
        <Form
          form={form}
          layout="vertical"
          name="paymentForm"
          validateMessages={validateMessages}
        >
          <div className="grid grid-cols-2 gap-x-6">
            <Form.Item name="client" label="Client">
              <Input disabled />
            </Form.Item>
            <Form.Item
              name="payment_date"
              label="Payment Date"
              rules={[{ required: true }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>
          </div>
          <div className="btn-border pt-1" />
          <div className="grid grid-cols-2 gap-x-6 mt-2.5">
            <Form.Item name="currency" label="Currency">
              <Select
                placeholder="Please select"
                className="w-[190px]"
                allowClear
                disabled
              >
                {currencies.map((item: any) => (
                  <Option key={item.id} value={item.name}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="exchange_rate"
              label="Exchange Rate (BDT)"
              initialValue={null}
            >
              <InputNumber
                className="w-full"
                disabled={invoiceDetails?.display_currency === Currency.BDT}
              />
            </Form.Item>
          </div>
          <div className="btn-border pt-1" />
          <div className="grid grid-cols-2 gap-x-6 mt-2.5">
            <Form.Item name="amount" label="Amount">
              <Input disabled />
            </Form.Item>
            <Form.Item
              name="payment_method"
              label="Payment Method"
              initialValue={PaymentMethod.BANK_DEPOSIT}
            >
              <Radio.Group>
                {Object.values(PaymentMethod).map((method) => (
                  <Radio key={method} value={method}>
                    {globalUtils.snakeCaseToCapitalize(method)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-6 mt-3.5">
            <Form.Item name="account_name" label="Bank Account Name">
              <Input disabled />
            </Form.Item>
            <Form.Item name="account_no" label="Bank Account No.">
              <InputNumber className="w-full" disabled />
            </Form.Item>
            <Form.Item name="bank_name" label="Bank">
              <Input disabled />
            </Form.Item>
            <Form.Item name="branch" label="Branch">
              <Input disabled />
            </Form.Item>
            <Form.Item name="routing_no" label="Routing Number">
              <InputNumber className="w-full" disabled />
            </Form.Item>
            <Form.Item name="phone" label="Phone Number">
              <InputNumber className="w-full" disabled />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
}

export default CreatePaymentModal;
